import * as React from 'react';
import * as _ from 'lodash';
import { Textbox } from '../inputs/textbox';
import { IAutocompleteResults } from '../../DTO/search';
import { Autocomplete } from './autocomplete';
import { SearchSuggestion } from './search-suggestion';

interface IProps {
    id: string;
    isEventList: boolean;
    onSearch: (val: string, isArchive: boolean) => void;
    onChange: (val: string, setAutocompleteResults?: (hits: string[]) => void) => void;
    searchButtonText?: string;
    placeHolder?: string;
    charsForAutocomplete?: number;
    searchText: string;
    searchSuggestionText: string;
    searchSuggestion: string;
    loading: boolean;
    autoCompleteResults?: IAutocompleteResults;
    setActiveHit?: (index: number) => void;
    setAutoCompleteActive?: (value: boolean) => void;
    isArchive: boolean;
    contextComponent: JSX.Element;
}

export function SearchBar({
    id,
    isEventList,
    onSearch,
    onChange,
    searchButtonText = 'Search',
    placeHolder = '',
    charsForAutocomplete = 1,
    searchText: searchTextProp,
    searchSuggestionText,
    searchSuggestion,
    loading,
    autoCompleteResults = null,
    setActiveHit,
    setAutoCompleteActive,
    isArchive,
    contextComponent
}: IProps) {
    const [searchText, setSearchText] = React.useState(searchTextProp);   
    const HasAutocomplete: boolean = autoCompleteResults != null;

    /// <summary>
    /// Intercepts special keys that handle autocomplete scrolling and selection
    /// </summary>
    const onInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        switch (e.keyCode) {
            //Up arrow
            //Move selected autocomplete result up one
            case 38: {
                e.preventDefault();
                if (autoCompleteResults.activeHit == -1) {
                    setActiveHit(autoCompleteResults.hits.length - 1);
                } else {
                    setActiveHit(autoCompleteResults.activeHit - 1);
                }
                break;
            }

            //Down arrow
            //Move selected autocomplete result down one
            case 40: {
                e.preventDefault();
                if (autoCompleteResults.activeHit == autoCompleteResults.hits.length - 1)
                    setActiveHit(-1);
                else
                    setActiveHit(autoCompleteResults.activeHit + 1);
                break;
            }

            //Enter key
            //If an autocomplete option is selected, change the search text to that, otherwise submit
            case 13: {
                let term = searchText;

                if (autoCompleteResults.activeHit > -1) {
                    term = autoCompleteResults.hits[autoCompleteResults.activeHit].query;
                    setSearchText(term);
                    setActiveHit(-1);
                }

                setAutoCompleteActive(false);
                onSearch(term, isArchive);
            }
        }
    };

    const onInputChange = (value: string) => {
        setSearchText(value);
        onChange(value);
    };

    const AutocompleteResultClicked = (value: string) => {
        setSearchText(value);
        setActiveHit(-1);
        setAutoCompleteActive(false);
        onSearch(value, isArchive);
    };

    const onBlur = () => {
        if (!HasAutocomplete) {
            return;
        }

        if (autoCompleteResults.activeHit == -1) {
            setAutoCompleteActive(false);
        }
    };

    const onFocus = () => {
        if (!HasAutocomplete) {
            return;
        }

        if (searchText.length >= charsForAutocomplete) {
            setAutoCompleteActive(true);
        }
    };

    const handleSearchButtonClick = () => {
        onSearch(searchText, isArchive);
    };

    const renderAutocomplete = () => {
        if (!autoCompleteResults.isActive || searchText.length < 3 || autoCompleteResults.hits.length === 0) {
            return;
        }

        return (
            <Autocomplete
                autocompleteResults={autoCompleteResults}
                searchTerm={searchText}
                onResultClick={AutocompleteResultClicked}
                onResultActive={setActiveHit}
                onMouseLeave={() => setActiveHit(-1)}
            />
        );
    };

    const onSuggestionClick = (suggestion: string) => {
        setSearchText(suggestion);
        onSearch(suggestion, isArchive);
    }
    return (
        <React.Fragment>
            <div className="adage-search-app-form">
                { !isEventList ? contextComponent : ""}
                <div className="adage-search-app-form-inner">
                    <div className="adage-search-app-form-textbox">
                        <Textbox
                            id={id}
                            onInput={(e) => { onInputChange(e.currentTarget.value) }}
                            onKeyDown={onInputKeyDown}
                            placeHolder={placeHolder}
                            onBlur={onBlur}
                            onFocus={onFocus}
                            value={searchText}
                        />

                        {renderAutocomplete()}
                    </div>

                    <button className="btn-secondary adage-search-app-form-button" type="submit" onClick={handleSearchButtonClick}>
                        <span>{searchButtonText}</span>
                    </button>
                </div>
            </div>
            <SearchSuggestion
                searchSuggestionText={searchSuggestionText}
                suggestion={searchSuggestion}
                loading={loading}
                onSuggestionClick={() => onSuggestionClick(searchSuggestion)}
            />
        </React.Fragment>
    );
};
